import { Link } from 'react-router-dom';
import { useState, useRef, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { ProductCard, Product } from '@/components/product/ProductCard';
import { RecommendedForYou } from '@/components/product/RecommendedForYou';
import { placeholder } from '@/data/catalogImages';
import clsx from 'clsx';
import { Seo } from '@/components/Seo';

/* Shared by the sell-CTA panel and its knockout headline. */
const SELL_CTA_IMAGE =
  'https://images.unsplash.com/photo-1567401893414-76b7b1e5a7a5?w=1800&h=920&fit=crop&auto=format&q=80';

const uw = (id: string) =>
  `https://images.unsplash.com/photo-${id}?w=1200&h=500&fit=crop&auto=format&q=80`;
/** Taller crop - keeps a full model/campaign shot from being cut off. */
const uwTall = (id: string) =>
  `https://images.unsplash.com/photo-${id}?w=900&h=1100&fit=crop&auto=format&q=85`;

/* ── demo data ──────────────────────────────────────────────────────── */




/* ── condition filter map ───────────────────────────────────────────── */
type ConditionLabel = 'All' | 'Never worn' | 'Worn once' | 'Worn twice' | 'Like new' | 'Well loved';
const CONDITION_LABELS: ConditionLabel[] = ['All', 'Never worn', 'Worn once', 'Worn twice', 'Like new', 'Well loved'];
const COND_WORN_MAP: Record<ConditionLabel, string[]> = {
  'All':        [],
  'Never worn': ['Never worn', 'New with tags'],
  'Worn once':  ['Worn once', 'Used once'],
  'Worn twice': ['Worn twice'],
  'Like new':   ['Never worn', 'New with tags', 'Worn once', 'Like new'],
  'Well loved': ['Worn a few times', 'Well loved', 'Worn a fair bit'],
};

/* ═══════════════════════════════════════════════════════════════════
   HOME  ·  single cream background, colour via cards
═══════════════════════════════════════════════════════════════════ */
export default function Home() {
  const [search, setSearch] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const [activeCondition, setActiveCondition] = useState<ConditionLabel>('All');

  // Pull a pool of real DB listings so every rail (Trending / Flash / New in)
  // shows live, buyable products. Falls back to the demo arrays only when the
  // backend is unreachable.
  const { data: products = [] } = useQuery<Product[]>({
    queryKey: ['products', 'home'],
    queryFn: async () => (await api.get('/products')).data,
    retry: false,
  });
  const havePool = products.length > 0;

  // Real marketplace figures for the stats row - no invented numbers.
  const { data: stats } = useQuery<{ activeListings: number; verifiedSellers: number }>({
    queryKey: ['stats', 'home'],
    queryFn: async () => (await api.get('/products/stats')).data,
    retry: false,
  });

  // Featured sellers pull from REAL accounts so "View wardrobe" always lands on
  // a live storefront - the old demo cards linked to handles that don't exist.
  const { data: sellersPool = [] } = useQuery<any[]>({
    queryKey: ['sellers', 'home'],
    queryFn: async () => (await api.get('/sellers')).data,
    retry: false,
  });
  const wardrobes: Wardrobe[] = sellersPool.slice(0, 4).map((s) => ({
    handle: `@${s.handle}`,
    name: s.name,
    listings: s.activeListings ?? 0,
    sales: s.soldCount ?? 0,
    rating: (s.rating ?? 0).toFixed(2),
    img: s.avatarUrl || placeholder(s.name ?? 'Seller', ''),
    items: (s.previewImages ?? []).slice(0, 3),
  }));

  const allFeatured = havePool ? products : [];
  const [editCat, setEditCat] = useState<string>('all');
  const featured = useMemo(() => {
    let pool = editCat === 'all' ? allFeatured : allFeatured.filter(p => (p as any).category === editCat);
    if (activeCondition !== 'All') {
      const wornMatch = COND_WORN_MAP[activeCondition];
      pool = pool.filter(p => wornMatch.some(w => p.worn?.toLowerCase().includes(w.toLowerCase())));
    }
    return pool.slice(0, 16);
  }, [allFeatured, editCat, activeCondition]);

  // Flash Drops rotate - a fresh random window of 6 each visit so the rail
  // never looks static. Recomputed when the pool loads or the page reloads.
  const flashItems = useMemo(() => {
    const pool = havePool ? products : [];
    if (pool.length <= 6) return pool.slice(0, 6);
    const start = Math.floor(Math.random() * (pool.length - 6));
    return pool.slice(start, start + 6);
  }, [havePool, products]);
  const newInItems = havePool ? products.slice(16, 28) : [];

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (search.trim()) window.location.href = `/c/all?q=${encodeURIComponent(search.trim())}`;
  };

  return (
    <div className="bg-cream-deep">
      <Seo canonicalPath="/" />

      {/* ── 1. HERO (full-bleed, bright) ─────────────────────────────── */}
      <section className="section-dark relative bg-ink text-cream overflow-hidden">
        {/* Higher opacity + centred crop on phones so the photo actually reads
            on a tall portrait hero; the softer desktop value returns at md. */}
        <img
          src="https://images.unsplash.com/photo-1483985988355-763728e1935b?w=1920&h=900&fit=crop&auto=format&q=80"
          alt="" aria-hidden
          className="absolute inset-0 w-full h-full object-cover object-center opacity-50 md:opacity-[0.34]"
        />
        {/* Lighter, less bottom-heavy overlay on mobile - the old near-black
            foot (ink/95) swallowed the image on a tall phone hero. */}
        <div className="absolute inset-0 bg-gradient-to-b from-ink/55 via-ink/50 to-ink/80 md:from-ink/70 md:via-ink/66 md:to-ink/95" />
        <div className="absolute inset-0 mashrabiya opacity-[0.04] pointer-events-none" />

        <div className="relative max-w-[1800px] mx-auto px-6 md:px-10 pt-20 pb-16 md:pt-28 md:pb-20">
          <div className="max-w-[640px] mx-auto text-center">

              <div className="inline-flex items-center gap-2 mb-6 rounded-full border border-cream/15 bg-cream/[0.04] px-3.5 py-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse" />
                <span className="stamp text-cream/70 text-[9px] tracking-[0.28em]">PRE-LOVED · VERIFIED · RECOMMERCE</span>
              </div>

              <h1 className="display text-[42px] sm:text-6xl md:text-[68px] leading-[0.98] tracking-[-0.02em] mb-5">
                Someone&apos;s barely worn.<br />
                <span className="text-gold italic">Now yours.</span>
              </h1>
              <p className="text-cream/60 text-[15px] md:text-[17px] leading-relaxed mb-6 max-w-[54ch] mx-auto">
                Behind every listing is a small story and a second chance. Find pieces that look brand new for a
                fraction of the price, from people we verify, with your money safe until it reaches you. Then let your
                own barely-touched things go to someone who will cherish them, and be paid for the passing on.
              </p>

              {/* dual CTAs - primary actions, kept high so they sit in the first screen */}
              <div className="flex flex-wrap justify-center gap-3 mb-8">
                <Link to="/c/all"
                  className="btn btn-pill btn-magnetic saffron-fill text-ink font-semibold px-7 py-3 hover:shadow-glow transition">
                  Shop pre-loved
                </Link>
                <Link to="/dashboard/sell"
                  className="btn btn-pill px-7 py-3 border border-cream/40 text-cream hover:bg-cream hover:text-ink transition">
                  Sell an item
                </Link>
              </div>

              {/* condition filter badges */}
              <div className="flex flex-wrap justify-center gap-2 mb-8">
                {CONDITION_LABELS.filter(c => c !== 'All').map(c => (
                  <button key={c} type="button"
                    onClick={() => setActiveCondition(activeCondition === c ? 'All' : c)}
                    className={`text-[11px] rounded-full px-3 py-1 transition-all border ${
                      activeCondition === c
                        ? 'border-gold bg-gold/15 text-gold'
                        : 'border-cream/20 hover:border-gold/60 hover:text-gold text-cream/70'
                    }`}>
                    {c}
                  </button>
                ))}
              </div>

              {/* big search bar */}
              <form onSubmit={handleSearch} className="relative max-w-[520px] mx-auto">
                <input
                  ref={searchRef}
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search brands, items, sellers&hellip;"
                  className="w-full h-14 rounded-full bg-cream text-ink placeholder:text-mute/60 text-sm pl-6 pr-36 outline-none border-2 border-transparent focus:border-gold transition-all shadow-luxe"
                />
                <button type="submit"
                  className="absolute right-2 top-2 h-10 px-5 rounded-full bg-gold text-ink text-sm font-semibold hover:brightness-105 transition">
                  Search
                </button>
              </form>

              {/* popular searches */}
              <div className="flex flex-wrap justify-center items-center gap-x-3 gap-y-1.5 mt-4">
                <span className="stamp text-cream/40 text-[9px]">POPULAR:</span>
                {['Acne jeans','Loewe bag','iPhone 14','Patagonia fleece','Vintage Levi\'s'].map(s => (
                  <Link key={s} to={`/c/all?q=${encodeURIComponent(s)}`}
                    className="text-[11px] text-cream/60 hover:text-gold transition underline underline-offset-2 decoration-cream/20 hover:decoration-gold">
                    {s}
                  </Link>
                ))}
              </div>


            </div>

            {/* stats row */}
            <div className="flex flex-wrap justify-center gap-x-10 gap-y-4 mt-12 pt-6">
              {[
                { n: stats ? stats.activeListings.toLocaleString() : '—', label: 'Verified items available' },
                { n: stats ? stats.verifiedSellers.toLocaleString() : '—', label: 'ID-verified sellers' },
                { n: '100%', label: 'Payment-protected' },
              ].map(s => (
                <div key={s.label} className="text-center">
                  <div className="display text-2xl text-gold">{s.n}</div>
                  <div className="stamp text-cream/45 text-[9px] mt-0.5">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
      </section>

      {/* ── 3. SHOP BY BUDGET ────────────────────────────────────────── */}
      <section className="max-w-[1800px] mx-auto px-6 md:px-10 pt-10">
        <div className="flex items-center gap-3 mb-4">
          <span className="stamp text-ink text-[9px]">SHOP BY BUDGET</span>
          <span className="h-px flex-1 bg-warmline" />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label:'Under £25',     href:'/c/all?max=25',   sub:'Everyday bargains',    img:uw('1445205170230-053b83016050'), tint:'from-[#8A3E2A]/85', pos:'object-center' },
            { label:'Under £50',     href:'/c/all?max=50',   sub:'Most popular range',   img:uw('1483985988355-763728e1935b'), tint:'from-[#A66018]/85', pos:'object-center' },
            { label:'Under £100',    href:'/c/all?max=100',  sub:'Premium pre-loved',    img:uw('1490481651871-ab68de25d43d'), tint:'from-[#4C5836]/85', pos:'object-center' },
            // Luxury: full editorial model shot, framed from the top so the
            // model reads like a campaign image rather than a cropped detail.
            { label:'Luxury resale', href:'/c/all?min=100',  sub:'Authenticated pieces', img:uwTall('1594938298603-c8148c4dae35'), tint:'from-[#221A12]/85', pos:'object-top' },
          ].map(p => (
            <Link key={p.label} to={p.href}
              className="relative overflow-hidden rounded-luxe bg-[#2a2016] px-5 py-5 flex flex-col gap-1 min-h-[128px] sm:min-h-[210px] justify-end shadow-luxe hover:shadow-glow hover:-translate-y-0.5 transition-all group">
              <img src={p.img} alt="" aria-hidden loading="lazy"
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                className={clsx('absolute inset-0 w-full h-full object-cover img-cohesive-tile group-hover:scale-105 transition-transform duration-700', p.pos)} />
              <div className={clsx('absolute inset-0 bg-gradient-to-t via-black/35 to-transparent', p.tint)} />
              <span className="relative display text-xl text-purewhite leading-tight">{p.label}</span>
              <span className="relative text-[11px] text-purewhite/70">{p.sub}</span>
              <span className="relative stamp text-[9px] mt-1 text-gold-light group-hover:translate-x-1 transition-transform">Shop now &rarr;</span>
            </Link>
          ))}
        </div>
      </section>

      {flashItems.length > 0 && (<>{/* ── 4. FLASH DROPS ───────────────────────────────────────────── */}
      <section className="max-w-[1800px] mx-auto px-6 md:px-10 pt-12">
        <SectionHeader
          eyebrow={
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-bad animate-pulse" />
              FLASH DROPS &middot; LIMITED TIME
            </span>
          }
          title=""
          eyebrowClass="text-red-600 font-bold"
          cta={{ label:'All flash drops', href:'/c/flash' }}
          ctaClass="text-red-600 font-bold"
        />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-4 gap-y-8 mt-8">
          {flashItems.map(p => <ProductCard key={p.id} product={p} />)}
        </div>
      </section></>)}


      {allFeatured.length > 0 && (<>{/* ── 6. TRENDING NOW ──────────────────────────────────────────── */}
      <section className="max-w-[1800px] mx-auto px-6 md:px-10 pt-12">
        <SectionHeader
          eyebrow="Hand-picked · verified sellers"
          eyebrowClass="text-ink font-bold"
          title=""
          titleClass="text-4xl md:text-[52px] text-ink"
          cta={{ label: 'All pieces', href:'/c/all' }}
          ctaClass="text-gold-dark font-bold"
        />
        {featured.length === 0 && (
          <p className="text-mute text-sm mt-6">Nothing here yet in this category. <button type="button" className="text-gold-dark underline" onClick={() => { setEditCat('all'); setActiveCondition('All'); }}>Show everything</button></p>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-8 mt-6">
          {featured.map(p => <ProductCard key={p.id} product={p} />)}
        </div>
      </section></>)}

      {/* ── 7. RECOMMENDED FOR YOU ───────────────────────────────────── */}
      <RecommendedForYou className="!bg-transparent !border-0 !pt-12 !pb-0" />

      {newInItems.length > 0 && (<>{/* ── 8. NEW IN 24H ────────────────────────────────────────────── */}
      <section className="max-w-[1800px] mx-auto px-6 md:px-10 pt-12">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="w-2 h-2 rounded-full bg-sage animate-pulse" />
              <span className="stamp text-gold-dark text-[9px]">LIVE INVENTORY</span>
            </div>
            <h2 className="display text-2xl md:text-3xl text-ink">New in the last 24 hours.</h2>
            <p className="text-mute text-sm mt-1">
              Freshly listed, verified, and ready to ship.
            </p>
          </div>
          <Link to="/c/new" className="btn btn-outline btn-sm self-start sm:self-end shrink-0">See all new arrivals &rarr;</Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-4 gap-y-8">
          {newInItems.map(p => <ProductCard key={p.id} product={p} />)}
        </div>
      </section></>)}

      {wardrobes.length > 0 && (
      <section className="max-w-[1800px] mx-auto px-6 md:px-10 pt-12">
        <SectionHeader eyebrow="Featured sellers" eyebrowClass="text-red-800 font-bold" title="Wardrobes worth following." cta={{ label:'Discover sellers', href:'/sellers' }} ctaClass="text-red-800 font-bold" />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mt-8">
          {wardrobes.map(w => <WardrobeCard key={w.handle} w={w} />)}
        </div>
      </section>
      )}

      {/* ── 10. SELL CTA (image background) ──────────────────────────── */}
      {/* One source of truth: the panel photo and the knockout headline must
          use the same image, or the letters stop lining up with the scene. */}
      <section className="max-w-[1800px] mx-auto px-4 md:px-10 py-14">
        <div className="section-dark relative rounded-luxe shadow-frame overflow-hidden text-cream">
          {/* Storytelling background - photographing an item to list (sell + AI) */}
          <img
            src={SELL_CTA_IMAGE}
            alt="" aria-hidden loading="lazy"
            onError={(e) => { (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1558769132-cb1aea458c5e?w=1800&h=920&fit=crop&auto=format&q=80'; }}
            className="absolute inset-0 w-full h-full object-cover"
          />
          {/* Deep, even scrim so the copy pops off the photograph */}
          <div className="absolute inset-0 bg-ink/70" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/85 to-ink/55" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/70 to-transparent" />
          <div className="absolute inset-0 mashrabiya opacity-[0.05] pointer-events-none" />
          <div className="relative grid md:grid-cols-2 gap-8 md:gap-12 items-center px-6 py-12 md:px-12 md:py-16">

            {/* left - pitch */}
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-gold px-3 py-1 text-ink text-[10px] font-bold tracking-widest mb-4">
                GOT SOMETHING TO SELL?
              </div>
              {/*
                Solid white, not knockout. Letting the photograph show through
                the letters looked good in isolation but the contrast changed
                with every part of the image, so the headline was only legible
                in places. A layered shadow keeps it readable over any photo:
                a tight dark halo for edge definition, a wider soft one for
                separation from busy areas.
              */}
              {/*
                Inner shadow on text - CSS text-shadow has no `inset`, so this
                uses the standard workaround: paint the glyphs from a white
                background clipped to the text, which makes the text-shadow
                render *inside* the letterforms rather than behind them.
                The outer glow then has to come from `filter: drop-shadow`,
                because text-shadow is already spent on the inset effect.
              */}
              <h2
                className="display text-3xl md:text-[44px] leading-[1.06] mb-3 text-purewhite"
                style={{ textShadow: '0 2px 14px rgba(0,0,0,0.65), 0 1px 2px rgba(0,0,0,0.55)' }}
              >
                Turn your wardrobe<br />into <span className="italic">real money.</span>
              </h2>
              <p className="text-cream text-[15px] max-w-[42ch] mb-6 leading-relaxed drop-shadow-[0_1px_6px_rgba(0,0,0,0.5)]">
                Photograph your item and our AI writes the title, price and a polished description for you.
                No listing fees, instant Stripe payouts, and a buyer base that respects your prices.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/dashboard/sell" className="btn btn-gold text-sm px-8 py-3 hover:shadow-glow transition">Start selling for free &rarr;</Link>
                <Link to="/ai-listing" className="btn btn-pill px-8 py-3 border border-cream/30 text-cream hover:bg-cream hover:text-ink transition text-sm">Try the AI listing tool</Link>
              </div>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-5 text-[12px] text-cream/90">
                <span className="flex items-center gap-1.5"><span className="text-gold-light font-bold">✓</span> No listing fees</span>
                <span className="flex items-center gap-1.5"><span className="text-gold-light font-bold">✓</span> Instant payouts</span>
                <span className="flex items-center gap-1.5"><span className="text-gold-light font-bold">✓</span> Verified buyers</span>
              </div>
            </div>

            {/* right - 3 quick steps */}
            <div className="grid gap-3">
              {[
                { icon:'📸', t:'Snap it',      s:'Photograph your item; that is all we need to begin.' },
                { icon:'✨', t:'AI lists it',  s:'Title, category, price and a polished description, written in seconds.' },
                { icon:'💸', t:'Get paid',     s:'Funds arrive via Stripe the moment your buyer confirms delivery.' },
              ].map((step, i) => (
                <div key={step.t} className="flex items-start gap-3.5 bg-white/[0.14] rounded-luxe border border-cream/25 backdrop-blur-sm px-4 py-3.5">
                  <span className="w-10 h-10 rounded-xl bg-gold text-ink grid place-items-center text-lg shrink-0">{step.icon}</span>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="stamp text-gold-light text-[9px]">STEP {i + 1}</span>
                      <span className="text-sm font-semibold text-purewhite">{step.t}</span>
                    </div>
                    <p className="text-[12px] text-cream/85 leading-snug mt-0.5">{step.s}</p>
                  </div>
                </div>
              ))}
            </div>

          </div>
        </div>
      </section>

    </div>
  );
}

/* ── Sub-components ───────────────────────────────────────────────────── */
interface SectionHeaderProps {
  eyebrow: React.ReactNode;
  title: string;
  titleClass?: string;
  eyebrowClass?: string;
  cta?: { label: string; href: string };
  ctaClass?: string;
}
function SectionHeader({ eyebrow, title, titleClass, eyebrowClass, cta, ctaClass }: SectionHeaderProps) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
      <div className="min-w-0">
        <div className={clsx('inline-flex items-center gap-2.5 stamp text-[9px] mb-3', eyebrowClass ?? 'text-gold-dark')}>
          <span className="w-6 h-px bg-current opacity-40" />
          {typeof eyebrow === 'string' ? eyebrow.toUpperCase() : eyebrow}
        </div>
        {title && <h2 className={clsx('display text-[26px] md:text-[40px] leading-[0.95] tracking-[-0.02em]', titleClass ?? 'text-ink')}>{title}</h2>}
      </div>
      {cta && (
        <Link to={cta.href} className={clsx('group/cta inline-flex items-center gap-1.5 self-start sm:self-end shrink-0 text-[13px] font-medium border-b-2 border-transparent hover:border-current transition-colors pb-0.5', ctaClass ?? 'text-gold-dark')}>
          {cta.label}
          <span className="transition-transform group-hover/cta:translate-x-0.5">&rarr;</span>
        </Link>
      )}
    </div>
  );
}

interface Wardrobe { handle: string; name: string; listings: string | number; sales: string | number; rating: string | number; img: string; items: string[] }

function WardrobeCard({ w }: { w: Wardrobe }) {
  return (
    <div className="bg-white border border-warmline rounded-luxe overflow-hidden hover:shadow-frame transition-all duration-300 group">
      <div className="grid grid-cols-3 h-28">
        {w.items.map((src, i) => (
          <div key={i} className="relative overflow-hidden">
            <img src={src} alt="" loading="lazy"
              className="absolute inset-0 w-full h-full object-cover img-cohesive group-hover:scale-[1.06] transition-transform duration-700 ease-souq" />
          </div>
        ))}
      </div>
      <div className="p-4">
        <div className="flex items-center gap-3 mb-3">
          <img src={w.img} alt={w.name} className="w-10 h-10 rounded-full object-cover border-2 border-gold/30" />
          <div className="min-w-0">
            <div className="font-semibold text-sm text-ink truncate">{w.name}</div>
            <div className="text-[11px] text-mute truncate">{w.handle}</div>
          </div>
        </div>
        <div className="flex justify-between text-[11px] text-mute mb-4">
          <span><span className="text-ink font-medium">{w.listings}</span> listings</span>
          <span><span className="text-ink font-medium">{w.sales}</span> sales</span>
          <span><span className="text-ink font-medium">&#9733; {w.rating}</span></span>
        </div>
        <Link to={`/seller/${w.handle.replace('@','')}`}
          className="btn btn-outline w-full text-center text-xs py-2">
          View wardrobe
        </Link>
      </div>
    </div>
  );
}
